
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Shop } from '@/lib/supabase/shops/types';
import { Product } from '@/lib/supabase/products/types';
import { getShops } from '@/lib/supabase/shops';
import { getProducts } from '@/lib/supabase/products';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageViewTracker } from '@/components/dashboard/shops/PageViewTracker';
import { ShoppingBag } from 'lucide-react';
import ShopHeader from '@/components/shop/ShopHeader';
import ShopFooter from '@/components/shop/ShopFooter';

const ShopHomePage = () => {
  const { shopSlug } = useParams<{ shopSlug: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Set the document title dynamically
  useDocumentTitle(shop?.name ? `${shop.name} | Scaloo` : 'Boutique | Scaloo');
  
  useEffect(() => {
    const fetchShopAndProducts = async () => {
      if (!shopSlug) return;
      
      try {
        setLoading(true);
        // Fetch all shops and find the one matching the slug
        const allShops = await getShops();
        const foundShop = allShops.find(s => 
          (s.slug && s.slug.toLowerCase() === shopSlug.toLowerCase()) || 
          (s.name && s.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === shopSlug.toLowerCase())
        );
        
        if (foundShop) {
          setShop(foundShop);
          
          // Fetch products for this shop
          const shopProducts = await getProducts(foundShop.id);
          setProducts(shopProducts);
        }
      } catch (error) {
        console.error('Error fetching shop data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchShopAndProducts();
  }, [shopSlug]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-32 h-6 bg-gray-300 dark:bg-gray-700 rounded mb-4"></div>
          <div className="w-48 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!shop) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-semibold mb-4">Boutique introuvable</h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">
          Nous n'avons pas trouvé la boutique que vous recherchez.
        </p>
        <Link to="/">
          <Button>Retour à l'accueil</Button>
        </Link>
      </div>
    );
  }
  
  // Prepare the categories URL
  const categoriesUrl = `/shop/${shopSlug}/categories`;
  
  return (
    <PageViewTracker shopId={shop.id} organizationId={shop.organization_id}>
      <div className="flex flex-col min-h-screen">
        <ShopHeader shop={shop} />
        
        <main className="flex-grow pt-16">
          {/* Hero section with shop information */}
          <section className="relative bg-gradient-to-b from-indigo-100 to-white dark:from-gray-800 dark:to-gray-900 py-16 md:py-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto text-center">
                {shop.logo_url ? (
                  <img 
                    src={shop.logo_url} 
                    alt={`${shop.name} logo`} 
                    className="h-24 mx-auto mb-6 rounded-lg shadow-sm"
                  />
                ) : (
                  <div className="h-24 w-24 mx-auto mb-6 bg-indigo-600 rounded-lg flex items-center justify-center text-white text-2xl font-bold">
                    {shop.name.charAt(0)}
                  </div>
                )}
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
                  {shop.name}
                </h1>
                
                {shop.description && (
                  <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                    {shop.description}
                  </p>
                )}
                
                {shop.announcement_text && (
                  <div className="bg-indigo-600 text-white p-4 rounded-lg shadow-md mt-6 mb-8 animate-fade-in">
                    {shop.announcement_text}
                  </div>
                )}
                
                <div className="mt-8 flex justify-center">
                  <Link to={categoriesUrl}>
                    <Button size="lg" className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" />
                      Voir toutes les catégories
                    </Button>
                  </Link>
                </div>
                
                <div className="flex justify-center space-x-4 mt-8">
                  {shop.instagram_url && (
                    <a href={shop.instagram_url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-pink-600 dark:text-gray-300 dark:hover:text-pink-500 transition-colors">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                      </svg>
                    </a>
                  )}
                  
                  {shop.facebook_url && (
                    <a href={shop.facebook_url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-500 transition-colors">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                      </svg>
                    </a>
                  )}
                  
                  {shop.tiktok_url && (
                    <a href={shop.tiktok_url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-black dark:text-gray-300 dark:hover:text-white transition-colors">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>
          
          {/* Products section */}
          <section className="py-12 bg-white dark:bg-gray-900">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  Nos produits
                </h2>
                <Link to={categoriesUrl}>
                  <Button variant="outline" className="flex items-center gap-2">
                    Voir toutes les catégories
                    <ShoppingBag className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
              
              {products.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-400">
                    Aucun produit disponible pour le moment.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {products.slice(0, 6).map((product) => {
                    // Create a slug from the product name
                    const productSlug = product.name ? 
                      product.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '';
                    
                    // Format URL for the product
                    const productUrl = `/shop/${shopSlug}/products/${productSlug}-${product.id}`;
                    
                    return (
                      <Card key={product.id} className="hover:shadow-lg transition-shadow duration-300">
                        <CardHeader className="p-0">
                          {product.media_urls && product.media_urls.length > 0 ? (
                            <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                              <img 
                                src={product.media_urls[0]} 
                                alt={product.name} 
                                className="w-full h-full object-cover object-center transition-transform hover:scale-105"
                              />
                            </div>
                          ) : (
                            <div className="aspect-video w-full bg-gray-200 dark:bg-gray-700 rounded-t-lg flex items-center justify-center">
                              <span className="text-gray-500 dark:text-gray-400">Pas d'image</span>
                            </div>
                          )}
                        </CardHeader>
                        <CardContent className="p-4">
                          <CardTitle className="mb-2 text-lg">{product.name}</CardTitle>
                          <p className="text-gray-700 dark:text-gray-300 line-clamp-2 mb-3 text-sm">
                            {product.description || 'Aucune description disponible'}
                          </p>
                          <p className="font-bold text-lg text-indigo-600 dark:text-indigo-400">
                            {product.price ? `${product.price} ${product.currency || '€'}` : 'Prix sur demande'}
                          </p>
                        </CardContent>
                        <CardFooter className="px-4 pb-4 pt-0">
                          <Link to={productUrl} className="w-full">
                            <Button className="w-full" variant="default">
                              Voir le produit
                            </Button>
                          </Link>
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {products.length > 6 && (
                <div className="mt-10 text-center">
                  <Link to={categoriesUrl}>
                    <Button size="lg">
                      Voir tous nos produits ({products.length})
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </section>
          
          {/* Contact section if shop has enabled forms */}
          {shop.show_contact_form && (
            <section id="contact" className="py-16 bg-gray-50 dark:bg-gray-800">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto text-center">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white">
                    {shop.contact_form_title || 'Contactez-nous'}
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-8">
                    Vous avez une question ? N'hésitez pas à nous contacter, nous vous répondrons dans les plus brefs délais.
                  </p>
                  
                  <div className="bg-white dark:bg-gray-900 p-8 rounded-lg shadow-sm">
                    <form className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Nom
                          </label>
                          <input
                            type="text"
                            id="name"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                            placeholder="Votre nom"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                            placeholder="Votre email"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Message
                        </label>
                        <textarea
                          id="message"
                          rows={5}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                          placeholder="Votre message"
                        ></textarea>
                      </div>
                      <Button className="w-full" size="lg">
                        Envoyer
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
        
        <ShopFooter shop={shop} shopSlug={shopSlug} />
      </div>
    </PageViewTracker>
  );
};

export default ShopHomePage;
